import React, { useEffect, useMemo, useState } from 'react';
import { Page, NotifyModal } from 'components';
import Header from 'components/Header/Header';
import { getEquipmentDetails, getEquipmentsByRegion, sendUpdateReleaseSelection, getUpdatesRelease } from 'api/updatesService';
import { getUnits } from 'api/companyService';
import EquipmentSelector from './components/EquipmentSelector';
import UpdatePreviewModal from './components/UpdatePreviewModal';
import Reload from 'components/Reload/Reload';

const isSimtEligible = (eq) => {
  const id = eq?.id || eq?.serialnumber;
  return typeof id === 'string' && id.toUpperCase().startsWith('SIMT-');
};

const UpdatesCharts = () => {
  const [loading, setLoading] = useState(true);
  const [equipments, setEquipments] = useState([]);
  const [regionGroups, setRegionGroups] = useState({ states: {}, cities: {} });
  const [unitGroups, setUnitGroups] = useState({}); // { unitId: { name, equipments: [], raw } }

  // Seleção e UI
  const [mode, setMode] = useState('equipment'); // 'state' | 'city' | 'equipment' | 'unit'
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const [selectedEquipments, setSelectedEquipments] = useState(new Set());
  const [showPreview, setShowPreview] = useState(false);
  const [previewPayload, setPreviewPayload] = useState(null);

  const [type, setType] = useState({ color: "", status: "" });
  const [modalShow, setModalShow] = useState(false);

  const handleAlertModalSuccess = (msg) => {
    setType({ "color": "#198754", "status": msg })
    setModalShow(true)
  }

  const handleAlertModalError = (msg) => {
    setType({ "color": "red", "status": msg })
    setModalShow(true)
  }

  // Mapear unidades -> equipamentos elegíveis existentes
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [details, groups, unitsRaw] = await Promise.all([
          getEquipmentDetails(),
          getEquipmentsByRegion(),
          getUnits().catch(() => [])
        ]);

        const equipmentsDetails = Array.isArray(details) ? details : [];
        setEquipments(equipmentsDetails);
        setRegionGroups(groups || { states: {}, cities: {} });

        const unitsArray = Array.isArray(unitsRaw)
          ? unitsRaw
          : (unitsRaw?.units || (unitsRaw ? [unitsRaw] : []));

        const uMap = {};
        unitsArray.forEach((unit) => {
          if (!unit) return;

          // Extrair e normalizar seriais de múltiplas fontes possíveis na unidade
          const extract = (val) => {
            const arr = Array.isArray(val) ? val : (val ? [val] : []);
            return arr.map(e => {
              if (typeof e === 'string') return e;
              return e?.serial || e?.serialnumber || e?.id;
            }).filter(Boolean);
          };

          const serialsA = extract(unit.equipments);
          const serialsB = extract(unit.localEquipament);
          const serialsC = extract(unit.equipamento); // Variável comum em PT

          const allNormalizedSerials = Array.from(new Set([...serialsA, ...serialsB, ...serialsC]))
            .map(s => String(s).trim().toUpperCase());

          const matched = allNormalizedSerials
            .map(sn => equipmentsDetails.find(eq => String(eq.id).trim().toUpperCase() === sn))
            .filter(Boolean)
            .filter(isSimtEligible);

          if (matched.length) {
            const key = unit._id || unit.name || Math.random().toString(36).slice(2);
            uMap[key] = {
              name: unit.name || key,
              equipments: matched,
              raw: unit
            };
          }
        });
        setUnitGroups(uMap);
      } catch (e) {
        setEquipments([]);
        setRegionGroups({ states: {}, cities: {} });
        console.error('Erro ao carregar dados:', e?.message || e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // Equipamentos elegíveis
  const eligibleEquipments = useMemo(
    () => equipments.filter(isSimtEligible),
    [equipments]
  );

  const equipmentUnitsMap = useMemo(() => {
    const map = {};

    Object.entries(unitGroups || {}).forEach(([unitId, unit]) => {
      const unitName = unit?.name || unitId;

      (unit?.equipments || []).forEach((equipment) => {
        if (!equipment?.id) return;

        if (!map[equipment.id]) {
          map[equipment.id] = [];
        }

        const alreadyLinked = map[equipment.id].some((item) => item.unitId === unitId);
        if (!alreadyLinked) {
          map[equipment.id].push({ unitId, unitName });
        }
      });
    });

    return map;
  }, [unitGroups]);

  const eligibleEquipmentDetails = useMemo(
    () => eligibleEquipments.map((equipment) => {
      const unitLinks = equipmentUnitsMap[equipment.id] || [];

      return {
        ...equipment,
        units: unitLinks,
        unitIds: unitLinks.map((item) => item.unitId),
        unitNames: unitLinks.map((item) => item.unitName)
      };
    }),
    [eligibleEquipments, equipmentUnitsMap]
  );

  const releaseCandidates = useMemo(
    () => eligibleEquipmentDetails,
    [eligibleEquipmentDetails]
  );

  const releaseCandidateIds = useMemo(
    () => new Set(releaseCandidates.map((equipment) => equipment.id)),
    [releaseCandidates]
  );

  const releaseCandidatesMap = useMemo(() => {
    const map = new Map();
    releaseCandidates.forEach((equipment) => map.set(equipment.id, equipment));
    return map;
  }, [releaseCandidates]);

  // Carregar último estado salvo (configurações de seleção previamente enviadas)
  useEffect(() => {
    if (!eligibleEquipmentDetails.length) return;

    const loadSavedConfig = async () => {
      try {
        const saved = await getUpdatesRelease();
        if (!saved) return;

        const record = Array.isArray(saved)
          ? [...saved].sort((a, b) => {
            const dateA = new Date(a?.createdAt || 0).getTime();
            const dateB = new Date(b?.createdAt || 0).getTime();
            return dateB - dateA;
          })[0]
          : saved;

        if (!record) return;

        const idsSet = new Set();
        if (Array.isArray(record.releasedEquipments) && record.releasedEquipments.length) {
          record.releasedEquipments.forEach((equipment) => {
            const id = equipment?.id || equipment?.serialnumber;
            if (id) idsSet.add(id);
          });
        } else if (Array.isArray(record.equipments) && record.equipments.length) {
          record.equipments.forEach((id) => idsSet.add(id));
        } else {
          if (Array.isArray(record.statesUpdate)) {
            record.statesUpdate.forEach((stateKey) => {
              const stateGroup = regionGroups.states[stateKey];
              stateGroup?.equipments?.forEach((eq) => idsSet.add(eq.id));
            });
          }

          if (Array.isArray(record.citysUpdate)) {
            record.citysUpdate.forEach((cityKey) => {
              const cityGroup = regionGroups.cities[cityKey];
              cityGroup?.equipments?.forEach((eq) => idsSet.add(eq.id));
            });
          }
        }

        if (idsSet.size) {
          const valid = new Set();
          idsSet.forEach((id) => {
            if (releaseCandidateIds.has(id)) valid.add(id);
          });
          setSelectedEquipments(valid);
        } else {
          setSelectedEquipments(new Set());
        }
      } catch (err) {
        console.error('Falha ao carregar configurações anteriores:', err?.message || err);
      }
    };

    loadSavedConfig();
  }, [eligibleEquipmentDetails, regionGroups, releaseCandidateIds]);

  // Filtro aplicado
  const normalized = (s) => (s || '').toString().toLowerCase();
  const filterFn = (eq) => {
    if (!filter) return true;
    const q = normalized(filter);
    return [
      eq.id,
      eq.name,
      eq.location,
      eq.city,
      eq.state,
      eq.currentVersion,
      ...(eq.unitNames || [])
    ].some((v) => normalized(v).includes(q));
  };

  const filteredEligible = releaseCandidates.filter(filterFn);

  // Helpers de região
  const getRegionKeys = () => {
    if (mode === 'state') return Object.keys(regionGroups.states || {});
    if (mode === 'city') return Object.keys(regionGroups.cities || {});
    if (mode === 'unit') return Object.keys(unitGroups || {});
    return [];
  };

  const getRegionName = (key) => {
    if (mode === 'state') return regionGroups.states[key]?.name || key;
    if (mode === 'city') return regionGroups.cities[key]?.name || key;
    if (mode === 'unit') return unitGroups[key]?.name || key;
    return key;
  };

  const getRegionEquipments = (key) => {
    const groupName = getRegionName(key);
    const q = normalized(filter);
    const groupMatches = !filter || normalized(groupName).includes(q);

    let rawList = [];
    if (mode === 'state') rawList = regionGroups.states[key]?.equipments || [];
    if (mode === 'city') rawList = regionGroups.cities[key]?.equipments || [];
    if (mode === 'unit') rawList = unitGroups[key]?.equipments || [];

    return (rawList || [])
      .map(eq => releaseCandidatesMap.get(eq.id))
      .filter(Boolean)
      .filter(eq => groupMatches || filterFn(eq));
  };

  const getRegionSelectedCount = (key) => {
    const list = getRegionEquipments(key);
    if (!list.length) return 0;
    return list.reduce((acc, eq) => acc + (selectedEquipments.has(eq.id) ? 1 : 0), 0);
  };

  // Handlers
  const toggleExpand = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleToggleEquipment = (id, checked) => {
    setSelectedEquipments((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  const handleToggleRegion = (key, checked) => {
    const list = getRegionEquipments(key);
    setSelectedEquipments((prev) => {
      const next = new Set(prev);
      list.forEach((eq) => { if (checked) next.add(eq.id); else next.delete(eq.id); });
      return next;
    });
  };

  // Utilitários de seleção em massa por aba
  const getFilteredRegionKeys = () => {
    return getRegionKeys().filter((k) => {
      const nameMatches = normalized(getRegionName(k)).includes(normalized(filter));
      if (nameMatches) return true;
      // Se o nome não bate, verifica se há algum equipamento interno que bate com o filtro
      return getRegionEquipments(k).length > 0;
    });
  };

  const selectAllRegionsVisible = () => {
    const keys = getFilteredRegionKeys();
    setSelectedEquipments((prev) => {
      const next = new Set(prev);
      keys.forEach((key) => {
        getRegionEquipments(key).forEach((eq) => next.add(eq.id));
      });
      return next;
    });
  };

  const clearRegionsVisible = () => {
    const keys = getFilteredRegionKeys();
    setSelectedEquipments((prev) => {
      const next = new Set(prev);
      keys.forEach((key) => {
        getRegionEquipments(key).forEach((eq) => next.delete(eq.id));
      });
      return next;
    });
  };

  const selectAllFilteredEquipments = () => {
    setSelectedEquipments((prev) => {
      const next = new Set(prev);
      filteredEligible.forEach((eq) => next.add(eq.id));
      return next;
    });
  };

  const clearFilteredEquipments = () => {
    setSelectedEquipments((prev) => {
      const next = new Set(prev);
      filteredEligible.forEach((eq) => next.delete(eq.id));
      return next;
    });
  };

  const visibleRegionsTotal = mode === 'equipment'
    ? 0
    : getFilteredRegionKeys().reduce((acc, key) => acc + getRegionEquipments(key).length, 0);

  // Montagem do payload e envio
  const buildPayload = () => {
    // statesUpdate: nomes das UFs visíveis e com seleção total? Especificamos todos os estados que têm pelo menos um selecionado
    const statesUpdate = Object.keys(regionGroups.states || {}).filter((uf) => {
      const list = (regionGroups.states[uf]?.equipments || []).filter((eq) => isSimtEligible(eq) && releaseCandidateIds.has(eq.id));
      return list.some((eq) => selectedEquipments.has(eq.id));
    });

    // citysUpdate: "UF - Cidade" onde há ao menos um selecionado
    const citysUpdate = Object.keys(regionGroups.cities || {}).filter((key) => {
      const list = (regionGroups.cities[key]?.equipments || []).filter((eq) => isSimtEligible(eq) && releaseCandidateIds.has(eq.id));
      return list.some((eq) => selectedEquipments.has(eq.id));
    });

    const releasedEquipments = Array.from(selectedEquipments)
      .map((id) => releaseCandidatesMap.get(id))
      .filter(Boolean)
      .map((equipment) => ({
        id: equipment.id,
        serialnumber: equipment.id,
        currentVersion: equipment.currentVersion || '',
        city: equipment.city || '',
        state: equipment.state || '',
        location: equipment.location || '',
        unitIds: equipment.unitIds || [],
        unitNames: equipment.unitNames || [],
        release: true
      }));

    const equipments = releasedEquipments.map((equipment) => equipment.id);
    const unitsUpdate = Object.keys(unitGroups || {}).filter((uk) => {
      const list = (unitGroups[uk]?.equipments || []).filter((eq) => isSimtEligible(eq) && releaseCandidateIds.has(eq.id));
      return list.some((eq) => selectedEquipments.has(eq.id));
    });
    const allSelected = equipments.length > 0 && equipments.length === releaseCandidates.length;
    return {
      allEquipmentsUpdate: allSelected,
      selectionMode: mode,
      statesUpdate,
      citysUpdate,
      unitsUpdate,
      equipments,
      releasedEquipments,
      selectionSummary: {
        totalEligibleEquipments: eligibleEquipmentDetails.length,
        totalSelectedEquipments: equipments.length,
      }
    };
  };

  const [submitting, setSubmitting] = useState(false);
  const handleSendUpdate = () => {
    const payload = buildPayload();

    if (!payload.equipments.length) {
      handleAlertModalError('Selecione ao menos um equipamento para liberar a atualização.');
      return;
    }

    setPreviewPayload(payload);
    setShowPreview(true);
  };

  const handleConfirmSend = async () => {
    try {
      setSubmitting(true);
      const payload = previewPayload || buildPayload();
      await sendUpdateReleaseSelection(payload);
      setShowPreview(false);
      handleAlertModalSuccess('Liberação de atualização salva com sucesso!');
    } catch (e) {
      handleAlertModalError(`Falha ao enviar atualização: ${e?.message || e}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page className="p-3" title="Atualizações de Curvas">

      <Header page={"Atualizações de Curvas"} />

      <NotifyModal
        type={type}
        show={modalShow}
        onHide={() => setModalShow(false)}
      />

      {loading && <Reload />}

      <>
        <EquipmentSelector
          mode={mode}
          setMode={setMode}
          filter={filter}
          setFilter={setFilter}
          totalEligibleCount={eligibleEquipmentDetails.length}
          selectedEquipments={selectedEquipments}
          filteredEligible={filteredEligible}
          handleToggleEquipment={handleToggleEquipment}
          selectAllFilteredEquipments={selectAllFilteredEquipments}
          clearFilteredEquipments={clearFilteredEquipments}
          visibleRegionsTotal={visibleRegionsTotal}
          selectAllRegionsVisible={selectAllRegionsVisible}
          clearRegionsVisible={clearRegionsVisible}
          getRegionKeys={getRegionKeys}
          normalized={normalized}
          getRegionName={getRegionName}
          getRegionEquipments={getRegionEquipments}
          getRegionSelectedCount={getRegionSelectedCount}
          handleToggleRegion={handleToggleRegion}
          expanded={expanded}
          toggleExpand={toggleExpand}
          submitting={submitting}
          handleSendUpdate={handleSendUpdate}
        />
        <UpdatePreviewModal
          show={showPreview}
          onHide={() => setShowPreview(false)}
          previewPayload={previewPayload}
          submitting={submitting}
          onConfirm={handleConfirmSend}
        />
      </>

    </Page>
  );
};

export default UpdatesCharts;

